var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useAlert } from 'components/alert/AlertStack';
import { Button } from 'components/Button';
import { FormInput } from 'components/form/FormInput';
import { toMoney } from 'components/Money';
import { Text } from 'components/Text';
import { RefundDealPaymentMutation } from 'graphql/Deal/mutations/RefundDealPaymentMutation';
import { React } from 'libs/react';
import './DealPaymentRefunds.scss';
function statusLabel(refund) {
    switch (refund.status) {
        case 'SUCCEEDED':
            return refund.provider === 'MANUAL' ? t('Recorded') : t('Refunded');
        case 'FAILED':
            return t('Failed');
        default:
            return t('Not confirmed by Stripe');
    }
}
/**
 * Refunds of a payment, and the form to add one on a cancelled order. The modal
 * stack keeps the payment it was opened with, so the mutation's answer is kept
 * here.
 *
 * @component DealPaymentRefunds
 * @since 1.0.0
 */
export function DealPaymentRefunds(props) {
    var _a;
    const { deal } = props;
    const apollo = useApolloClient();
    const alert = useAlert();
    const [payment, setPayment] = useState(props.payment);
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [processing, setProcessing] = useState(false);
    const refunds = (_a = payment.refunds) !== null && _a !== void 0 ? _a : [];
    const canRefund = (deal === null || deal === void 0 ? void 0 : deal.status) === 'CANCELLED' &&
        !deal.orderIn &&
        payment.status === 'COMPLETED' &&
        payment.refundableAmount > 0;
    if (refunds.length === 0 && !canRefund) {
        return null;
    }
    const value = Number(amount) || 0;
    const invalid = value <= 0 || value > payment.refundableAmount;
    const submit = (input) => __awaiter(this, void 0, void 0, function* () {
        setProcessing(true);
        try {
            const result = yield apollo.mutate({
                mutation: RefundDealPaymentMutation,
                variables: { input: Object.assign({ paymentId: payment.id }, input) }
            });
            if (result.data) {
                setPayment(Object.assign(Object.assign({}, payment), result.data.refundDealPayment));
            }
            setAmount('');
            setReason('');
        }
        catch (error) {
            alert.present({ error: error });
        }
        finally {
            setProcessing(false);
        }
    });
    return (React.createElement("div", { className: "deal-payment-refunds" },
        React.createElement(Text, { className: "deal-payment-refunds-title", value: t('Refunds') }),
        refunds.map((refund) => (React.createElement("div", { className: "deal-payment-refunds-row", key: refund.id },
            React.createElement("div", { className: "deal-payment-refunds-row-infos" },
                React.createElement(Text, { value: `${toMoney(refund.amount)} · ${statusLabel(refund)}` }),
                (refund.error || refund.reason) && (React.createElement(Text, { className: "deal-payment-refunds-row-note", value: refund.error || refund.reason }))),
            refund.status === 'PENDING' && (React.createElement(Button, { size: "small", label: t('Retry'), processing: processing, onPress: () => submit({ refundId: refund.id }) }))))),
        canRefund && (React.createElement("div", { className: "deal-payment-refunds-form" },
            React.createElement(Text, { className: "deal-payment-refunds-row-note", value: payment.refundProvider === 'MANUAL'
                    ? t('To refund in Mews or outside Centralio. Only recorded here.')
                    : t('Up to {{amount}}').replace('{{amount}}', toMoney(payment.refundableAmount)) }),
            React.createElement(FormInput, { type: "number", size: "small", suffix: "$", min: "0", max: String(payment.refundableAmount), value: amount, onChange: setAmount }),
            React.createElement(FormInput, { size: "small", placeholder: t('Refund reason (optional)'), value: reason, onChange: setReason }),
            React.createElement(Button, { size: "small", style: "primary", label: payment.refundProvider === 'MANUAL'
                    ? t('Record refund')
                    : t('Refund'), disabled: invalid, processing: processing, onPress: () => submit({ amount: value, reason: reason.trim() || null }) })))));
}
